import StudentLessonMonitorHost from './components/StudentLessonMonitorHost';
import ReviewStudyScreen from './components/ReviewStudyScreen';
import { useLessonReviewMode } from './hooks/useLessonReviewMode';
import './styles/lessonMonitor.css';
// src/App.jsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AssessmentProvider, useAssessmentContext } from './contexts/AssessmentContext';
import { AdminProvider } from './contexts/AdminContext';
import AccessCodeModal from './components/AccessCodeModal';
import AssessmentLogin from './components/AssessmentLogin';
import AssessmentQuestion from './components/AssessmentQuestion';
import AssessmentComplete from './components/AssessmentComplete';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import { getVersionLabel } from './config/appMeta';
import { stopActiveSpeech } from './utils/ttsRuntime';

console.info(`[AssessNow] ${getVersionLabel()}`);

function AppContent() {
  const {
    assessmentData,
    accessCode,
    isComplete,
    isAccessVerified,
    setAccessCode,
    resetAssessment,
    setIsAccessVerified,
  } = useAssessmentContext();

  const [userRole, setUserRole] = useState(null);
  const [isAdminMode, setIsAdminMode] = useState(false);           // teacher-only “switch to admin” flag
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);   // admin dashboard credentials OK
  const [error, setError] = useState(null);
  const logoutTransitionInFlight = useRef(false);
  const reviewMode=useLessonReviewMode(isAccessVerified&&userRole==='subuser',accessCode);
  const [reviewSticky,setReviewSticky]=useState(false);
  useEffect(()=>{if(reviewMode.enabled&&assessmentData&&!isComplete)setReviewSticky(true);if(!isAccessVerified)setReviewSticky(false);},[reviewMode.enabled,assessmentData,isComplete,isAccessVerified]);

  /* ---------- helpers ---------- */
  const handleLogout = useCallback(() => {
    if (logoutTransitionInFlight.current) return;
    logoutTransitionInFlight.current = true;
    stopActiveSpeech('logout');
    setIsAdminLoggedIn(false);
    resetAssessment();
    setAccessCode('');
    setIsAccessVerified(false);
    setUserRole(null);
    setIsAdminMode(false);
  }, [resetAssessment, setAccessCode, setIsAccessVerified]);

  useEffect(() => {
    const handleExpiredAuthentication = () => {
      const dashboardSession = userRole === 'admin' || (userRole === 'teacher' && isAdminMode);
      if (dashboardSession && isAdminLoggedIn) {
        // A protected dashboard session can be renewed from AdminLogin. Keep
        // the already verified access-code boundary intact instead of ejecting
        // the user from the application altogether.
        setIsAdminLoggedIn(false);
        return;
      }
      handleLogout();
    };
    window.addEventListener('auth:expired', handleExpiredAuthentication);
    return () => window.removeEventListener('auth:expired', handleExpiredAuthentication);
  }, [handleLogout, isAdminLoggedIn, isAdminMode, userRole]);

  useEffect(() => {
    const stopForPageExit = () => stopActiveSpeech('application-backgrounded');
    const stopWhenHidden = () => {
      if (document.visibilityState === 'hidden') stopForPageExit();
    };
    window.addEventListener('pagehide', stopForPageExit);
    window.addEventListener('beforeunload', stopForPageExit);
    document.addEventListener('visibilitychange', stopWhenHidden);
    return () => {
      window.removeEventListener('pagehide', stopForPageExit);
      window.removeEventListener('beforeunload', stopForPageExit);
      document.removeEventListener('visibilitychange', stopWhenHidden);
      stopActiveSpeech('application-unmounted');
    };
  }, []);

  /* ---------- error boundary ---------- */
  if (error) {
    return (
      <div className="fixed inset-0 bg-red-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-red-600 text-xl font-bold mb-4">Application Error</h2>
          <p className="text-gray-600 mb-4">{error.message}</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition-colors"
          >
            Reload Application
          </button>
        </div>
      </div>
    );
  }

  /* ---------- access-code gate ---------- */
  if (!isAccessVerified) {
    return (
      <div className="relative">
        <AccessCodeModal
          onSuccess={(userData) => {
            logoutTransitionInFlight.current = false;
            setAccessCode(userData.accessCode);
            setUserRole(userData.role);
            setIsAccessVerified(true);
          }}
          onError={(err) => setError(err)}
        />
      </div>
    );
  }

  /* ---------- admin / teacher-admin branch ---------- */
  const isAdminBranch =
    userRole === 'admin' || (userRole === 'teacher' && isAdminMode);

  if (isAdminBranch) {
    return (
      <AdminProvider>
        {!isAdminLoggedIn ? (
          <AdminLogin userRole={userRole} onLogin={() => setIsAdminLoggedIn(true)} />
        ) : (
          <AdminDashboard onLogout={handleLogout} />
        )}
      </AdminProvider>
    );
  }

  /* ---------- student branch ---------- */
  const view=reviewSticky||(reviewMode.enabled&&assessmentData&&!isComplete)
    ? <ReviewStudyScreen enabled={reviewMode.enabled}/>
    : isComplete?<AssessmentComplete/>:!assessmentData?<AssessmentLogin onAdminLogin={userRole==='teacher'?()=>setIsAdminMode(true):null}/>:<AssessmentQuestion/>;
  return <>{userRole==='subuser'&&<StudentLessonMonitorHost/>}{view}</>;

}

export default function App() {
  return (
    <AssessmentProvider>
      <AppContent />
    </AssessmentProvider>
  );
}
